import { calculateBalance, goalGap, memberLifecycle, recommendOffer } from "../../shared/domain";
import { seedOffers } from "../../shared/seed";
import type {
  ActivitySnapshot,
  AnalyticsEvent,
  Attribution,
  Credit,
  EarnSnapshot,
  Goal,
  MemberOffer,
  MemberSession,
  Offer,
  Redemption,
  SupportCase,
} from "../../shared/types";

interface DemoUser {
  id: string;
  email: string;
  password: string;
  emailVerified: boolean;
}

interface DemoAttempt {
  id: string;
  token: string;
  offerId: string;
  offerTitle: string;
  rewardCents: number;
  status: "started" | "completion_reported" | "credited_pending" | "credited_available" | "expired" | "rejected";
  startedAt: string;
}

interface DemoState {
  user?: DemoUser;
  signedIn: boolean;
  attribution?: Attribution;
  goal?: Goal;
  offers: Offer[];
  attempts: DemoAttempt[];
  credits: Credit[];
  cases: SupportCase[];
  redemptions: Redemption[];
  events: AnalyticsEvent[];
  earnViewDays: string[];
}

const DEMO_KEY = "bucks_demo_v2";
const isDemo = import.meta.env.VITE_DEMO_MODE === "true" || import.meta.env.MODE === "test";

function initialState(): DemoState {
  return {
    signedIn: false,
    offers: structuredClone(seedOffers),
    attempts: [],
    credits: [],
    cases: [],
    redemptions: [],
    events: [],
    earnViewDays: [],
  };
}

function readDemo(): DemoState {
  const raw = localStorage.getItem(DEMO_KEY);
  if (!raw) return initialState();
  try {
    return JSON.parse(raw) as DemoState;
  } catch {
    return initialState();
  }
}

function writeDemo(state: DemoState) {
  localStorage.setItem(DEMO_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent("bucks:demo-change"));
}

function uid(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`;
}

function demoSession(state = readDemo()): MemberSession | null {
  if (!state.signedIn || !state.user) return null;
  return {
    user: { id: state.user.id, email: state.user.email, emailVerified: state.user.emailVerified },
    needsGoal: !state.goal,
  };
}

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: init.body ? { "content-type": "application/json", ...init.headers } : init.headers,
  });
  const payload = (await response.json()) as T & { error?: { code: string; message: string } };
  if (!response.ok) throw new Error(payload.error?.message || "Request failed.");
  return payload;
}

function currentOfferState(state: DemoState, offer: Offer): MemberOffer["memberState"] {
  const credit = state.credits.find((item) => item.offerId === offer.id && item.status !== "reversed");
  if (credit?.status === "pending") return "pending";
  if (credit?.status === "available") return offer.repeatability === "one_time" ? "completed" : "earned";
  const attempt = state.attempts.find((item) => item.offerId === offer.id && item.status === "started");
  return attempt ? "started" : "available";
}

function requireDemoUser(state: DemoState) {
  if (!state.signedIn || !state.user) throw new Error("Please sign in to continue.");
  return state.user;
}

export const api = {
  demo: isDemo,

  async captureAttribution(attribution: Attribution) {
    if (isDemo) {
      const state = readDemo();
      if (!state.attribution) state.attribution = attribution;
      writeDemo(state);
      return;
    }
    await http("/api/attribution/touch", { method: "POST", body: JSON.stringify(attribution) });
  },

  async session(): Promise<MemberSession | null> {
    if (isDemo) return demoSession();
    const response = await http<{ user: MemberSession["user"] | null; needsGoal?: boolean }>("/api/auth/session");
    return response.user ? { user: response.user, needsGoal: Boolean(response.needsGoal) } : null;
  },

  async signup(email: string, password: string): Promise<MemberSession> {
    if (isDemo) {
      const state = readDemo();
      if (state.user && state.user.email.toLowerCase() === email.toLowerCase()) throw new Error("An account may already exist. Try signing in.");
      state.user = { id: uid("user"), email: email.toLowerCase(), password, emailVerified: true };
      state.signedIn = true;
      writeDemo(state);
      return demoSession(state)!;
    }
    return http("/api/auth/signup", { method: "POST", body: JSON.stringify({ email, password, accepted: true }) });
  },

  async login(email: string, password: string): Promise<MemberSession> {
    if (isDemo) {
      const state = readDemo();
      if (!state.user || state.user.email !== email.toLowerCase() || state.user.password !== password) throw new Error("Email or password is incorrect.");
      state.signedIn = true;
      writeDemo(state);
      return demoSession(state)!;
    }
    return http("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  },

  async requestPasswordReset(email: string): Promise<{ ok: true; demoToken?: string }> {
    if (isDemo) {
      const state = readDemo();
      if (state.user?.email === email.toLowerCase()) {
        const token = uid("reset");
        sessionStorage.setItem("bucks_demo_reset", token);
        return { ok: true, demoToken: token };
      }
      return { ok: true };
    }
    return http("/api/auth/password-reset/request", { method: "POST", body: JSON.stringify({ email }) });
  },

  async confirmPasswordReset(token: string, password: string): Promise<void> {
    if (isDemo) {
      if (sessionStorage.getItem("bucks_demo_reset") !== token) throw new Error("This reset link is invalid or expired.");
      const state = readDemo();
      if (!state.user) throw new Error("This reset link is invalid or expired.");
      state.user.password = password;
      state.signedIn = false;
      writeDemo(state);
      sessionStorage.removeItem("bucks_demo_reset");
      return;
    }
    await http("/api/auth/password-reset/confirm", { method: "POST", body: JSON.stringify({ token, password }) });
  },

  async logout() {
    if (isDemo) {
      const state = readDemo();
      state.signedIn = false;
      writeDemo(state);
      return;
    }
    await http("/api/auth/logout", { method: "POST", body: "{}" });
  },

  async setGoal(amountCents: number): Promise<Goal> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      if (state.goal) state.goal.status = "replaced";
      state.goal = { id: uid("goal"), amountCents, status: "active" };
      writeDemo(state);
      return state.goal;
    }
    return http("/api/member/goals", { method: "POST", body: JSON.stringify({ amountCents }) });
  },

  async earn(): Promise<EarnSnapshot> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      const today = new Date().toISOString().slice(0, 10);
      if (!state.earnViewDays.includes(today)) state.earnViewDays.push(today);
      const balance = calculateBalance(state.credits, state.redemptions);
      if (state.goal?.status === "active" && balance.availableCents >= state.goal.amountCents) {
        state.goal.status = "reached";
        state.goal.reachedAt = new Date().toISOString();
      }
      const offers: MemberOffer[] = state.offers
        .filter((offer) => offer.status === "active")
        .map((offer) => ({
          ...offer,
          memberState: currentOfferState(state, offer),
          attemptId: state.attempts.find((item) => item.offerId === offer.id && item.status === "started")?.id,
        }));
      const gapCents = goalGap(state.goal || null, balance.availableCents);
      const recommendation = recommendOffer(offers, gapCents);
      writeDemo(state);
      return {
        balance,
        goal: state.goal || null,
        gapCents,
        offers,
        recommendation,
        cashOutEnabled: true,
        lifecycle: memberLifecycle(state.credits, state.earnViewDays.length),
      };
    }
    return http("/api/member/earn");
  },

  async startOffer(offerId: string, placement: string, recommendationReason?: string) {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      const offer = state.offers.find((item) => item.id === offerId && item.status === "active");
      if (!offer) throw new Error("This offer is no longer available.");
      if (offer.repeatability === "one_time" && state.credits.some((item) => item.offerId === offerId && item.status !== "reversed")) {
        throw new Error("You already completed this offer.");
      }
      const existing = state.attempts.find((item) => item.offerId === offerId && item.status === "started");
      if (existing) return { attemptId: existing.id, redirectUrl: `/demo/complete/${existing.id}` };
      const attempt: DemoAttempt = {
        id: uid("attempt"),
        token: uid("token"),
        offerId,
        offerTitle: offer.title,
        rewardCents: offer.rewardCents,
        status: "started",
        startedAt: new Date().toISOString(),
      };
      state.attempts.unshift(attempt);
      state.events.push({ name: "offer_attempt_created", properties: { offerId, placement, recommendationReason } });
      writeDemo(state);
      return { attemptId: attempt.id, redirectUrl: `/demo/complete/${attempt.id}` };
    }
    return http<{ attemptId: string; redirectUrl: string }>(`/api/offers/${offerId}/attempts`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), placement, recommendationReason }),
    });
  },

  async completeDemoAttempt(attemptId: string, pending = false): Promise<Credit> {
    if (!isDemo) throw new Error("Demo completion is unavailable in production.");
    const state = readDemo();
    requireDemoUser(state);
    const attempt = state.attempts.find((item) => item.id === attemptId);
    if (!attempt) throw new Error("Attempt not found.");
    const existing = state.credits.find((item) => item.attemptId === attemptId);
    if (existing) return existing;
    const stamp = new Date().toISOString();
    const credit: Credit = {
      id: uid("credit"),
      offerId: attempt.offerId,
      attemptId,
      title: attempt.offerTitle,
      amountCents: attempt.rewardCents,
      status: pending ? "pending" : "available",
      source: "automated",
      createdAt: stamp,
      availableAt: pending ? undefined : stamp,
    };
    attempt.status = pending ? "credited_pending" : "credited_available";
    state.credits.unshift(credit);
    writeDemo(state);
    return credit;
  },

  async activity(): Promise<ActivitySnapshot> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      return { credits: state.credits, attempts: state.attempts };
    }
    return http("/api/member/activity");
  },

  async createMissingCredit(attemptId: string, explanation: string, confirmationReference?: string): Promise<SupportCase> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      if (state.cases.some((item) => item.attemptId === attemptId && ["open", "investigating"].includes(item.status))) {
        throw new Error("A case is already open for this attempt.");
      }
      const attempt = state.attempts.find((item) => item.id === attemptId);
      if (!attempt) throw new Error("Attempt not found.");
      const supportCase: SupportCase = {
        id: uid("case"),
        attemptId,
        offerTitle: attempt.offerTitle,
        explanation,
        status: "open",
        createdAt: new Date().toISOString(),
      };
      state.cases.unshift(supportCase);
      writeDemo(state);
      return supportCase;
    }
    return http("/api/member/support/missing-credit", {
      method: "POST",
      body: JSON.stringify({ attemptId, explanation, confirmationReference }),
    });
  },

  async redemptions(): Promise<{ enabled: boolean; redemptions: Redemption[] }> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      return { enabled: true, redemptions: state.redemptions };
    }
    return http("/api/member/redemptions");
  },

  async requestRedemption(amountCents: number, payoutEmail: string): Promise<Redemption> {
    if (isDemo) {
      const state = readDemo();
      requireDemoUser(state);
      const balance = calculateBalance(state.credits, state.redemptions);
      if (amountCents > balance.availableCents) throw new Error("Your available balance is too low for that request.");
      const [name, domain] = payoutEmail.split("@");
      const redemption: Redemption = {
        id: uid("redemption"),
        amountCents,
        status: "requested",
        payoutEmailMasked: `${name.slice(0, 2)}***@${domain}`,
        createdAt: new Date().toISOString(),
      };
      state.redemptions.unshift(redemption);
      writeDemo(state);
      return redemption;
    }
    return http("/api/member/redemptions", { method: "POST", body: JSON.stringify({ amountCents, payoutEmail }) });
  },

  async track(event: AnalyticsEvent) {
    if (isDemo) {
      const state = readDemo();
      state.events.push(event);
      writeDemo(state);
      return;
    }
    try {
      await http("/api/events", { method: "POST", body: JSON.stringify(event) });
    } catch {
      // Analytics is deliberately non-blocking.
    }
  },

  async opsData(key: string) {
    if (isDemo) {
      const state = readDemo();
      return { offers: state.offers, users: state.user ? [{ id: state.user.id, email: state.user.email, gross_available: calculateBalance(state.credits, state.redemptions).availableCents }] : [], cases: state.cases, redemptions: state.redemptions, credits: state.credits };
    }
    const headers = { "x-ops-key": key };
    const [offers, users, cases, redemptions, credits] = await Promise.all([
      http<{ offers: Offer[] }>("/api/ops/offers", { headers }),
      http<{ users: unknown[] }>("/api/ops/users?q=", { headers }),
      http<{ cases: unknown[] }>("/api/ops/cases", { headers }),
      http<{ redemptions: unknown[] }>("/api/ops/redemptions", { headers }),
      http<{ credits: Credit[] }>("/api/ops/credits", { headers }),
    ]);
    return { offers: offers.offers, users: users.users, cases: cases.cases, redemptions: redemptions.redemptions, credits: credits.credits };
  },

  async opsCreateOffer(key: string, input: Pick<Offer, "slug" | "title" | "shortDescription" | "rewardCents" | "destinationUrl">) {
    if (isDemo) {
      const state = readDemo();
      state.offers.unshift({
        id: uid("offer"), ...input, status: "draft", priority: 0, estimatedMinutes: null,
        quickWin: false, purchaseRequired: false, repeatability: "one_time", deviceEligibility: "all",
        newUsersOnly: false, badges: [], ctaLabel: `Earn $${(input.rewardCents / 100).toFixed(2)}`,
        completionTerms: "Complete the qualifying partner action.", creditTimingCopy: "Confirmation timing varies by offer.",
      });
      writeDemo(state);
      return;
    }
    await http("/api/ops/offers", { method: "POST", headers: { "x-ops-key": key }, body: JSON.stringify(input) });
  },

  async opsUpdateOffer(key: string, offerId: string, patch: Partial<Offer>) {
    if (isDemo) {
      const state = readDemo();
      const index = state.offers.findIndex((item) => item.id === offerId);
      if (index >= 0) state.offers[index] = { ...state.offers[index], ...patch };
      writeDemo(state);
      return;
    }
    await http(`/api/ops/offers/${offerId}`, { method: "PATCH", headers: { "x-ops-key": key }, body: JSON.stringify(patch) });
  },

  async opsCreateCredit(key: string, userId: string, amountCents: number, title: string) {
    if (isDemo) {
      const state = readDemo();
      if (state.user?.id !== userId) throw new Error("User not found.");
      state.credits.unshift({ id: uid("credit"), title, amountCents, status: "available", source: "manual", createdAt: new Date().toISOString(), availableAt: new Date().toISOString() });
      writeDemo(state);
      return;
    }
    await http("/api/ops/credits", { method: "POST", headers: { "x-ops-key": key }, body: JSON.stringify({ userId, amountCents, title, status: "available", reasonCode: "operator_verified" }) });
  },

  async opsReverseCredit(key: string, creditId: string) {
    if (isDemo) {
      const state = readDemo();
      const credit = state.credits.find((item) => item.id === creditId);
      if (credit) { credit.status = "reversed"; credit.reversedAt = new Date().toISOString(); }
      writeDemo(state);
      return;
    }
    await http(`/api/ops/credits/${creditId}/reverse`, { method: "POST", headers: { "x-ops-key": key }, body: JSON.stringify({ reasonCode: "operator_correction" }) });
  },

  async opsMakeCreditAvailable(key: string, creditId: string) {
    if (isDemo) {
      const state = readDemo();
      const credit = state.credits.find((item) => item.id === creditId);
      if (credit?.status === "pending") { credit.status = "available"; credit.availableAt = new Date().toISOString(); }
      writeDemo(state);
      return;
    }
    await http(`/api/ops/credits/${creditId}/available`, { method: "POST", headers: { "x-ops-key": key }, body: "{}" });
  },

  async opsResolveCase(key: string, caseId: string, status: "resolved_credited" | "resolved_no_credit" | "closed_duplicate") {
    if (isDemo) {
      const state = readDemo();
      const item = state.cases.find((entry) => entry.id === caseId);
      if (item) item.status = status;
      writeDemo(state);
      return;
    }
    await http(`/api/ops/cases/${caseId}/resolve`, { method: "POST", headers: { "x-ops-key": key }, body: JSON.stringify({ status, reasonCode: "operator_review" }) });
  },

  async opsRedemptionStatus(key: string, redemptionId: string, status: "approved" | "paid" | "rejected") {
    if (isDemo) {
      const state = readDemo();
      const item = state.redemptions.find((entry) => entry.id === redemptionId);
      if (item) item.status = status;
      writeDemo(state);
      return;
    }
    await http(`/api/ops/redemptions/${redemptionId}/status`, { method: "POST", headers: { "x-ops-key": key }, body: JSON.stringify({ status }) });
  },

  resetDemo() {
    localStorage.removeItem(DEMO_KEY);
    window.dispatchEvent(new CustomEvent("bucks:demo-change"));
  },
};

export const __demo = { initialState, readDemo, writeDemo, key: DEMO_KEY };
